import Box from "@mui/material/Box";
import React, { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import CardActions from "@mui/material/CardActions";

import GetAppIcon from "@mui/icons-material/GetApp";
import IconButton from "@mui/material/IconButton";
import SortIcon from "@mui/icons-material/Sort";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import CheckIcon from "@mui/icons-material/Check";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SwatchesPicker from "../Shared/SwatchesPicker";
import Dialog from "@mui/material/Dialog";
import Tooltip from "@mui/material/Tooltip";
import {
  actionDataGroupUpdated,
  getDefaultColor,
  actionDataRemoveDataGroup,
  actionDataMaxGroupSizeUpdated,
  actionDataSelectFeatureRequested,
  filtersToQueryParam,
} from "./dataRedux";
import { xlsx } from "../Shared/downloader";
import { CopyToClipboard } from "react-copy-to-clipboard";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import _ from "lodash";
import { actionLayerUpdate } from "../Layer";
import { toText } from "./Filters";

const display_ = (theme) => ({
  "& .MuiMenu-paper": {
    minWidth: 130,
  },
});

export default function TableTools({ data }) {
  return (
    <CardActions
      disableSpacing
      style={{ paddingTop: "0px", marginTop: "-8px" }}
    >
      <Remove data={data} />
      <Download data={data} />
      <Sort data={data} />
      <GroupBy data={data} />
      <FiltersToLink data={data} />
      <ColorPicker data={data} />
    </CardActions>
  );
}

function Download({ data }) {
  const onClick = () => {
    const filters = data.filters.map((filter) => toText(filter)).join(" + ");
    const name = `Search Result - ${filters}`;
    const features = _.sortBy(data.rooms, ["bld_descrshort", "floor", "rmnbr"]);
    xlsx({ name, features, directPropPath: true });
  };

  return (
    <Tooltip title="Download Result">
      <IconButton style={{ marginLeft: "auto" }} size="small" onClick={onClick}>
        <GetAppIcon />
      </IconButton>
    </Tooltip>
  );
}

function Remove({ data }) {
  const dispatch = useDispatch();
  const { colorLayerId } = data;

  const handleClick = () => {
    dispatch(actionDataRemoveDataGroup(data));
    dispatch(
      actionDataSelectFeatureRequested({
        type: "room",
        data: [],
        layerId: colorLayerId,
      }),
    );
    dispatch(actionDataMaxGroupSizeUpdated(-1));
  };

  return (
    <Button color="secondary" onClick={handleClick}>
      REMOVE
    </Button>
  );
}

function Sort({ data }) {
  const dispatch = useDispatch();

  const iconStyle = {
    fontSize: "1.2rem",
    marginLeft: "4px",
  };

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [sortBy, setSortBy] = React.useState("building");
  const [direction, setDirection] = useState("ascending");

  const prevSort = useRef(null);
  useEffect(() => {
    prevSort.current = sortBy;
  });

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (sortType) => () => {
    setSortBy(sortType);
    if (sortType === prevSort.current) {
      const newDirection =
        direction === "ascending" ? "descending" : "ascending";
      setDirection(newDirection);
      dispatch(
        actionDataGroupUpdated({
          id: data.id,
          sortBy: sortType,
          sortDirection: newDirection,
        }),
      );
    } else {
      dispatch(actionDataGroupUpdated({ id: data.id, sortBy: sortType }));
    }
  };

  return (
    <>
      <Tooltip title="Sort By">
        <IconButton onClick={handleClick} size="small">
          <SortIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        sx={display_}
      >
        <MenuItem onClick={handleSelect("building")}>
          <Typography variant="overline">Building</Typography>
          {sortBy === "building" && direction === "ascending" && (
            <ArrowUpwardIcon style={iconStyle} />
          )}
          {sortBy === "building" && direction === "descending" && (
            <ArrowDownwardIcon style={iconStyle} />
          )}
        </MenuItem>
        <MenuItem onClick={handleSelect("room")}>
          <Typography variant="overline">Room</Typography>
          {sortBy === "room" && direction === "ascending" && (
            <ArrowUpwardIcon style={iconStyle} />
          )}
          {sortBy === "room" && direction === "descending" && (
            <ArrowDownwardIcon style={iconStyle} />
          )}
        </MenuItem>
        <MenuItem onClick={handleSelect("sqft")}>
          <Typography variant="overline">Sqft</Typography>
          {sortBy === "sqft" && direction === "ascending" && (
            <ArrowUpwardIcon style={iconStyle} />
          )}
          {sortBy === "sqft" && direction === "descending" && (
            <ArrowDownwardIcon style={iconStyle} />
          )}
        </MenuItem>
      </Menu>
    </>
  );
}

function GroupBy({ data }) {
  const dispatch = useDispatch();

  const iconStyle = {
    fontSize: "1.2rem",
    marginLeft: "4px",
  };

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [groupBy, setGroupBy] = React.useState(data.groupBy);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (group) => () => {
    setGroupBy(group);
    dispatch(actionDataGroupUpdated({ id: data.id, groupBy: group }));
  };

  return (
    <>
      <Tooltip title="Content Display">
        <Button onClick={handleClick} aria-label="List By">
          List By
        </Button>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        sx={display_}
      >
        <Tooltip title="Building-Floors">
          <MenuItem onClick={handleSelect("building-floor")}>
            <Typography variant="overline">Building</Typography>
            {groupBy === "building-floor" && <CheckIcon style={iconStyle} />}
          </MenuItem>
        </Tooltip>
        <Tooltip title="Building-Floor-Sqft">
          <MenuItem onClick={handleSelect("building-floor-sqft")}>
            <Typography variant="overline">Floor</Typography>
            {groupBy === "building-floor-sqft" && (
              <CheckIcon style={iconStyle} />
            )}
          </MenuItem>
        </Tooltip>
        <Tooltip title="Building-Floor-Room-Sqft">
          <MenuItem onClick={handleSelect("room")}>
            <Typography variant="overline">Room</Typography>
            {groupBy === "room" && <CheckIcon style={iconStyle} />}
          </MenuItem>
        </Tooltip>
      </Menu>
    </>
  );
}

const color_ = (theme) => ({
  width: 20,
  height: 20,
  borderRadius: "50%",
  "&:hover": {
    cursor: "pointer",
  },
});

function FiltersToLink({ data }) {
  const defaultTitle = "Create URL from Search";
  const [title, setTitle] = useState(defaultTitle);
  const urlText = filtersToQueryParam(data.filters);

  const onCopyHandler = () => {
    setTitle("Copied to Clipboard!");
    setTimeout(() => {
      setTitle(defaultTitle);
    }, 1500);
  };

  return (
    <Tooltip describeChild title={title}>
      <span>
        <CopyToClipboard text={urlText} onCopy={onCopyHandler}>
          <IconButton size="small" aria-label={title}>
            <FileCopyIcon />
          </IconButton>
        </CopyToClipboard>
      </span>
    </Tooltip>
  );
}

function ColorPicker({ data }) {
  const dispatch = useDispatch();
  const { id, colorLayerId, color } = data;
  const [isVisible, setIsVisible] = useState(false);

  const onChangeComplete = (color) => {
    const newColor = color && color.hex ? color.hex : color;
    dispatch(actionDataGroupUpdated({ id, color: newColor }));
    dispatch(
      actionLayerUpdate({
        type: "room-select",
        id: colorLayerId,
        field: "paint",
        value: { attr: "fill-color", val: newColor },
      }),
    );
  };

  const handleShowColor = () => {
    setIsVisible(true);
  };
  const handleClose = () => {
    setIsVisible(false);
  };
  const resetHandler = () => {
    onChangeComplete(getDefaultColor(colorLayerId));
    handleClose();
  };

  return (
    <Box>
      <Tooltip describeChild title="Highlight Color">
        <Box
          style={{ backgroundColor: color }}
          onClick={handleShowColor}
          sx={color_}
        ></Box>
      </Tooltip>
      <Dialog onClose={handleClose} open={isVisible}>
        <Box style={{ textAlign: "center" }}>
          <SwatchesPicker color={color} onChangeComplete={onChangeComplete} />
          <Button onClick={resetHandler}>Reset</Button>
        </Box>
      </Dialog>
    </Box>
  );
}
